/**
 * Date formatting utilities for consistent date/time display across the app.
 * All functions handle UTC conversion and return empty strings on error.
 */

/**
 * Formats a date as "Jan 15, 2024"
 * @param dateInput - Date object, ISO string, or undefined
 * @returns Formatted date string or empty string if invalid
 */
export const formatDate = (dateInput?: string | Date): string => {
  if (!dateInput) return "";

  try {
    let date: Date;

    if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      // Add 'Z' to force UTC interpretation if not already present
      const utcDateString = dateInput.endsWith("Z")
        ? dateInput
        : dateInput + "Z";
      date = new Date(utcDateString);
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

/**
 * Formats a time as "2:30 PM"
 * @param dateString - ISO date string
 * @returns Formatted time string or empty string if invalid
 */
export const formatTime = (dateString: string): string => {
  if (!dateString) return "";

  try {
    // Add 'Z' to force UTC interpretation if not already present
    const utcDateString = dateString.endsWith("Z")
      ? dateString
      : dateString + "Z";
    const date = new Date(utcDateString);

    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (error) {
    console.error("Error formatting time:", error);
    return "";
  }
};

/**
 * Formats today's date as "Jan 15, 2024"
 * @returns Today's date in formatted string
 */
export const formatTodayDate = (): string => formatDate(new Date());
